import { useEffect } from "react";

interface SEOHeadProps {
  title: string;
  description: string;
  keywords?: string;
  canonicalPath?: string;
  jsonLd?: object | object[];
}

function setOrCreateMeta(selector: string, attrKey: string, attrVal: string, content: string) {
  let el = document.querySelector<HTMLMetaElement>(selector);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attrKey, attrVal);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

const SITE_URL = "https://willstrusts-junlawfirm.com";

export default function SEOHead({ title, description, keywords, canonicalPath, jsonLd }: SEOHeadProps) {
  const siteUrl = (import.meta.env.VITE_SITE_URL as string | undefined) || SITE_URL;
  const canonical = `${siteUrl}${canonicalPath ?? ""}`;

  useEffect(() => {
    document.title = title;

    setOrCreateMeta('meta[name="description"]', "name", "description", description);
    if (keywords) setOrCreateMeta('meta[name="keywords"]', "name", "keywords", keywords);
    setOrCreateMeta('meta[name="last-modified"]', "name", "last-modified", new Date().toISOString());

    // Open Graph
    setOrCreateMeta('meta[property="og:title"]', "property", "og:title", title);
    setOrCreateMeta('meta[property="og:description"]', "property", "og:description", description);
    setOrCreateMeta('meta[property="og:url"]', "property", "og:url", canonical);
    setOrCreateMeta('meta[property="og:type"]', "property", "og:type", "website");
    setOrCreateMeta('meta[property="og:site_name"]', "property", "og:site_name", "Law Office of Hong-min Jun");

    // Twitter
    setOrCreateMeta('meta[name="twitter:card"]', "name", "twitter:card", "summary_large_image");
    setOrCreateMeta('meta[name="twitter:title"]', "name", "twitter:title", title);
    setOrCreateMeta('meta[name="twitter:description"]', "name", "twitter:description", description);

    // Canonical
    let canonicalEl = document.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (!canonicalEl) {
      canonicalEl = document.createElement("link");
      canonicalEl.setAttribute("rel", "canonical");
      document.head.appendChild(canonicalEl);
    }
    canonicalEl.setAttribute("href", canonical);
  }, [title, description, keywords, canonical]);

  useEffect(() => {
    if (!jsonLd) return;
    const id = "seo-json-ld";
    let scriptEl = document.getElementById(id) as HTMLScriptElement | null;
    if (!scriptEl) {
      scriptEl = document.createElement("script");
      scriptEl.id = id;
      scriptEl.type = "application/ld+json";
      document.head.appendChild(scriptEl);
    }
    scriptEl.textContent = JSON.stringify(jsonLd);
    return () => {
      const el = document.getElementById(id);
      if (el) el.remove();
    };
  }, [jsonLd]);

  return null;
}
